<template>
	<n-tooltip v-if="useUnknownFallback && meta.status === 'UNKNOWN'" class="text-fs-xs! max-w-50! px-1.5! py-1!">
		<template #trigger>
			<n-tag
				round
				:size
				:color
				class="text-fs-sm w-5.5! cursor-help! justify-center px-0! font-medium"
				:class="className || ''"
			>
				<PatientStatusIcon :meta :size />
			</n-tag>
		</template>
		{{ $t("patients.patient_status.unknown_tooltip") }}
	</n-tooltip>
	<n-tooltip v-else :disabled="!showInfo" class="text-fs-xs! px-1.5! py-1!">
		<template #trigger>
			<n-tag
				:round
				:size
				:color
				class="text-fs-sm font-medium"
				:class="[className || '', showInfo && 'cursor-help!']"
			>
				<div class="flex items-center gap-1">
					<PatientStatusIcon :meta :size />
					{{ label }}
				</div>
			</n-tag>
		</template>
		{{ statusLabel }}
	</n-tooltip>
</template>

<script setup lang="ts">
import type { PatientMeta } from "@/types/patient"
import { NTag, NTooltip } from "naive-ui"
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { dFormat } from "@/utils/dayjs"
import { getGestationalLabel } from "@/utils/pregnancyCalculator"
import PatientStatusIcon from "../../PatientStatusIcon.vue"

const {
	meta,
	size = "small",
	showInfo,
	class: className,
	useUnknownFallback = true
} = defineProps<{
	meta: PatientMeta
	round?: boolean
	showInfo?: boolean
	class?: string
	size?: "small" | "medium" | "large" | "tiny"
	useUnknownFallback?: boolean
}>()

const { t } = useI18n()

const statusLabel = computed(() => t(`patients.patient_status.${meta.status.toLowerCase()}`))

const label = computed(() => {
	if (!showInfo) {
		return statusLabel.value
	}

	switch (meta.status) {
		case "PREGNANCY":
			return meta.gestational_period ? getGestationalLabel(meta.gestational_period, { t }) : statusLabel.value
		case "FERTILITY":
		case "MENOPAUSE":
			return meta.last_period ? dFormat(meta.last_period, "date") : statusLabel.value
		case "VOLUNTARY_ABORTION":
		case "NATURAL_ABORTION":
		case "UNKNOWN_ABORTION":
			return meta.abortion_date ? dFormat(meta.abortion_date, "date") : statusLabel.value
		case "POST_PARTUM":
			return meta.pregnancy_ended ? dFormat(meta.pregnancy_ended, "date") : statusLabel.value
		default:
			return statusLabel.value
	}
})

const color = computed(() => {
	switch (meta.status) {
		case "PREGNANCY":
			return {
				color: "var(--status-pregnancy-400-color)",
				borderColor: "var(--status-pregnancy-500-color)",
				textColor: "var(--status-pregnancy-600-color)"
			}

		case "FERTILITY":
			return {
				color: "var(--status-fertility-400-color)",
				borderColor: "var(--status-fertility-500-color)",
				textColor: "var(--status-fertility-600-color)"
			}
		case "MENOPAUSE":
			return {
				color: "var(--status-menopause-400-color)",
				borderColor: "var(--status-menopause-500-color)",
				textColor: "var(--status-menopause-600-color)"
			}
		case "VOLUNTARY_ABORTION":
		case "NATURAL_ABORTION":
		case "UNKNOWN_ABORTION":
			return {
				color: "var(--grey-500-color)",
				borderColor: "var(--grey-700-color)",
				textColor: "var(--color-white)"
			}
		case "POST_PARTUM":
			return {
				color: "var(--grey-50-color)",
				borderColor: "var(--grey-300-color)",
				textColor: "var(--grey-900-color)"
			}
		default:
			return undefined
	}
})
</script>
